import { FC, useContext, useEffect, useMemo, useState } from "react";

import { Checkbox } from "capitalrx-components-2.0/lib/forms";
import { Icon, Link } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { formularyEquivalentsAltsSubstitutionsListAPI } from "@/api";
import { ThemeSelectorContext } from "@/context";
import { hexToRgbColor } from "@/utils";
import type { RawFormularyRecord } from "./DrugCoverage";
import { DrugResultCard } from "./DrugResultCard";
import styles from "./DrugAlternativesView.module.scss";

type AlternativeSource = "equivalent" | "alternative";

interface DrugAlternativeResponseItem {
  brand_name?: string;
  ndc?: string;
  gpi?: string;
  tier?: string;
  tier_original?: string;
  quantity_limit?: string;
  source?: AlternativeSource;
  tier_override?: string | null;
  quantity_limit_override?: string | null;
}

interface DrugAlternativeRecord {
  record: RawFormularyRecord;
  source: AlternativeSource;
}

const normalizeAlternativeSource = (source?: string): AlternativeSource => {
  if (source === "equivalent") {
    return "equivalent";
  }
  return "alternative";
};

const normalizeTierOriginal = (tierOriginal: string | undefined, tier: string): string => {
  return tierOriginal || (tier ? `Tier ${tier}` : "");
};

const normalizeQuantityLimit = (quantityLimit?: string | null): string | undefined => {
  if (!!quantityLimit && quantityLimit !== "null") {
    return quantityLimit;
  }
  return undefined;
};

interface DrugAlternativesViewProps {
  selectedDrug: RawFormularyRecord;
  records: RawFormularyRecord[];
  isMobile: boolean;
  onBack: () => void;
}

export const DrugAlternativesView: FC<DrugAlternativesViewProps> = ({
  selectedDrug,
  records,
  isMobile,
  onBack,
}) => {
  const { primary_color } = useContext(ThemeSelectorContext);
  const [showEquivalents, setShowEquivalents] = useState(true);
  const [isLoadingAlternatives, setIsLoadingAlternatives] = useState(false);
  const [alternativesError, setAlternativesError] = useState<string | null>(null);
  const [alternativeRecords, setAlternativeRecords] = useState<DrugAlternativeRecord[]>([]);

  useEffect(() => {
    let isActive = true;

    const loadAlternatives = async () => {
      try {
        setIsLoadingAlternatives(true);
        setAlternativesError(null);

        const response = await formularyEquivalentsAltsSubstitutionsListAPI({
          formulary_id: selectedDrug.formulary_id,
          ndc: selectedDrug.ndc,
          include_equivalents: true,
        });
        const responseResults: DrugAlternativeResponseItem[] = Array.isArray(response?.results)
          ? response.results
          : [];
        const recordsByNdc = new Map(records.map((record) => [record.ndc, record]));

        const mappedRecords = responseResults
          .map((item) => {
            if (!item?.ndc) {
              return null;
            }

            const source = normalizeAlternativeSource(item.source);
            const tier = item.tier_override || item.tier || "";
            const quantityLimit =
              item.quantity_limit_override ||
              item.quantity_limit;
            const normalizedQuantityLimit = normalizeQuantityLimit(quantityLimit);
            const normalizedTierOriginal = normalizeTierOriginal(item.tier_original, tier);

            const existingRecord = recordsByNdc.get(item.ndc);
            if (existingRecord) {
              return {
                record: {
                  ...existingRecord,
                  tier: tier || existingRecord.tier,
                  tier_original: existingRecord.tier_original || normalizedTierOriginal,
                  quantity_limit: normalizedQuantityLimit || existingRecord.quantity_limit,
                },
                source,
              };
            }

            return {
              record: {
                effective_date: "",
                end_date: "",
                start_date: "",
                termination_date: "",
                formulary_id: selectedDrug.formulary_id,
                label_name: item.brand_name || item.ndc,
                ndc: item.ndc,
                tier,
                tier_original: normalizedTierOriginal,
                brand_name: item.brand_name,
                quantity_limit: normalizedQuantityLimit,
                gpi: item.gpi,
                edits: [],
              } as RawFormularyRecord,
              source,
            };
          })
          .filter((record): record is DrugAlternativeRecord => !!record)
          .filter((record) => record.record.ndc !== selectedDrug.ndc);

        if (!isActive) {
          return;
        }

        setAlternativeRecords(mappedRecords);
      } catch (err) {
        if (!isActive) {
          return;
        }

        console.error("Failed to load drug alternatives:", err);
        setAlternativesError("Unable to load drug alternatives.");
        setAlternativeRecords([]);
      } finally {
        if (isActive) {
          setIsLoadingAlternatives(false);
        }
      }
    };

    loadAlternatives();

    return () => {
      isActive = false;
    };
  }, [records, selectedDrug]);

  const equivalentRecords = useMemo(() => {
    return alternativeRecords
      .filter((record) => record.source === "equivalent")
      .map((record) => record.record);
  }, [alternativeRecords]);

  const nonEquivalentAlternativeRecords = useMemo(() => {
    return alternativeRecords
      .filter((record) => record.source === "alternative")
      .map((record) => record.record);
  }, [alternativeRecords]);

  return (
    <Stack spacing="none" className={styles.Container}>
      <Shelf spacing="mini">
        <Icon name="arrow-left" color={hexToRgbColor(primary_color)} />
        <Link
          to="#"
          className={styles.BackLinkText}
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
        >
          Back to Search
        </Link>
        <Shelf.Spacer />
      </Shelf>

      <Subheading className={styles.PageTitle} style={{ fontSize: "1.5rem", lineHeight: "2rem" }}>
        {selectedDrug.label_name} Alternatives
      </Subheading>

      <div className={styles.Spacer32} />

      <Stack spacing="medium">
        <Semibold className={styles.SectionTitle}>Selected Drug</Semibold>
        <DrugResultCard
          record={selectedDrug}
          isMobile={isMobile}
          showDrugActions={false}
          className={styles.SelectedDrugCard}
        />
      </Stack>

      <div className={styles.Spacer24} />

      <Checkbox
        className={styles.ShowEquivalentsCheckbox}
        unpadded
        label="Show Equivalents"
        onChange={(value) => setShowEquivalents(!!value)}
        value={showEquivalents}
      />

      {isLoadingAlternatives && <Body>Loading alternatives...</Body>}
      {alternativesError && <Body>{alternativesError}</Body>}

      {!!showEquivalents && (
        <>
          <div className={styles.Spacer24} />
          <Stack spacing="medium">
            <Semibold className={styles.SectionTitle}>Equivalents</Semibold>
            {equivalentRecords.length > 0 ? (
              <Stack spacing="medium" className={styles.CardList}>
                {equivalentRecords.map((record) => (
                  <DrugResultCard
                    key={`${record.ndc}-${record.tier}-${record.network}`}
                    record={record}
                    isMobile={isMobile}
                    showDrugActions={false}
                  />
                ))}
              </Stack>
            ) : (
              <Body>No equivalent drugs found.</Body>
            )}
          </Stack>
        </>
      )}

      <div className={styles.Spacer24} />

      <Stack spacing="medium">
        <Semibold className={styles.SectionTitle}>Alternatives</Semibold>
        {nonEquivalentAlternativeRecords.length > 0 ? (
          <Stack spacing="medium" className={styles.CardList}>
            {nonEquivalentAlternativeRecords.map((record) => (
              <DrugResultCard
                key={`${record.ndc}-${record.tier}-${record.network}`}
                record={record}
                isMobile={isMobile}
                showDrugActions={false}
              />
            ))}
          </Stack>
        ) : (
          <Body>No alternative drugs found.</Body>
        )}
      </Stack>
    </Stack>
  );
};
